import type { LoginInput, SetupInput, TokenPair } from '@tasks/contracts/auth';
import { SETUP_TOKEN_HEADER } from '@tasks/contracts/auth';
import { ApiError } from '#lib/api-client';
import { RefreshRejected } from './refresh-coordinator.js';

const BASE = '/api/v1';

const post = async <T>(
  path: string,
  body: unknown,
  headers: Record<string, string> = {},
): Promise<T> => {
  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json', ...headers },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new ApiError(await problemOf(response));
  return (await response.json()) as T;
};

const problemOf = async (response: Response) => {
  const fallback = {
    type: 'https://tasks/errors/internal',
    title: response.statusText,
    status: response.status,
  };
  return ((await response.json().catch(() => null)) ?? fallback) as typeof fallback;
};

export const login = (input: LoginInput): Promise<TokenPair> => post('/auth/login', input);

/** `POST /api/v1/auth/vitto`: the host's single-use pass for the product session. The caller validates the answer (`vitto-door.ts`). */
/** `roster` is the host's SIGNED members statement, transported as it came: the API verifies it against the pass or ignores it whole. */
export const loginWithVitto = (grant: string, roster?: string): Promise<unknown> =>
  post('/auth/vitto', roster === undefined ? { grant } : { grant, roster });

export const runSetup = (input: SetupInput, token: string): Promise<TokenPair> =>
  post('/setup', input, { [SETUP_TOKEN_HEADER]: token });

export const setupPending = async (): Promise<boolean> => {
  const response = await fetch(`${BASE}/setup`, { headers: { accept: 'application/json' } });
  if (!response.ok) return false;
  return ((await response.json()) as { pending: boolean }).pending;
};

/**
 * Un 401 significa que el refresh ya no vale y hay que volver a login; cualquier otro fallo
 * (red, 5xx) se propaga tal cual para que el coordinador conserve el token guardado.
 */
export const refresh = async (refreshToken: string): Promise<TokenPair> => {
  try {
    return await post<TokenPair>('/auth/refresh', { refreshToken });
  } catch (error) {
    if (error instanceof ApiError && error.problem.status === 401) {
      throw new RefreshRejected(error.problem.title, error.problem.type);
    }
    throw error;
  }
};
